import { useState, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQuoteModal } from '../../../context/QuoteModalContext';

interface FAQItem {
  question: string;
  answer: React.ReactNode;
  keywords: string[];
}

interface FAQGroup {
  id: string;
  title: string;
  icon: string;
  faqs: FAQItem[];
}

const faqGroups: FAQGroup[] = [
  {
    id: 'priority',
    title: 'Top Questions',
    icon: 'ri-star-line',
    faqs: [
      {
        question: 'What is the difference between a Stretch Tent and a Bedouin Tent?',
        answer: (
          <p>
            They are essentially the same product. &ldquo;Bedouin Tent&rdquo; is the original term inspired by traditional Bedouin nomadic structures from North Africa and the Middle East, while &ldquo;Stretch Tent&rdquo; is the modern industry name. Both refer to the same flexible, waterproof fabric structures that can be configured into many different shapes using poles and tension — making them exceptionally versatile for events of all kinds.
          </p>
        ),
        keywords: ['bedouin', 'difference', 'stretch', 'traditional', 'same'],
      },
      {
        question: 'Are stretch tents waterproof?',
        answer: (
          <p>
            Yes. Our tents are manufactured from high-quality waterproof, UV-resistant, fire-rated fabric designed specifically for outdoor events. When installed correctly with proper tensioning by our professional team, they provide excellent protection from rain while maintaining an elegant open-air feel that traditional marquees simply cannot match.
          </p>
        ),
        keywords: ['waterproof', 'rain', 'fabric', 'water', 'weather', 'protection'],
      },
      {
        question: 'Do you deliver throughout Texas?',
        answer: (
          <p>
            Yes. We are based in <strong>Austin, Texas</strong> and regularly install throughout the state including Dallas, Houston, San Antonio, Fort Worth, Waco, Fredericksburg, Dripping Springs, the Texas Hill Country and surrounding areas. We also travel nationwide for larger events and commercial projects — <Link to="/contact" className="text-teal-500 hover:text-teal-600 underline font-medium">get in touch</Link> to discuss your location.
          </p>
        ),
        keywords: ['texas', 'delivery', 'austin', 'dallas', 'houston', 'nationwide', 'location'],
      },
    ],
  },
  {
    id: 'sizing',
    title: 'Sizing & Capacity',
    icon: 'ri-ruler-line',
    faqs: [
      {
        question: 'How many guests can a stretch tent hold?',
        answer: (
          <div className="space-y-3">
            <p>Capacity depends on your layout and event style. Here are approximate examples:</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 pr-4 font-semibold text-gray-700">Tent Size</th>
                    <th className="text-left py-2 pr-4 font-semibold text-gray-700">Seated</th>
                    <th className="text-left py-2 font-semibold text-gray-700">Standing</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-100"><td className="py-2 pr-4">30&prime; × 40&prime;</td><td className="py-2 pr-4">Up to 80</td><td className="py-2">Up to 120</td></tr>
                  <tr className="border-b border-gray-100"><td className="py-2 pr-4">40&prime; × 60&prime;</td><td className="py-2 pr-4">Up to 150</td><td className="py-2">Up to 220</td></tr>
                  <tr className="border-b border-gray-100"><td className="py-2 pr-4">65&prime; × 50&prime;</td><td className="py-2 pr-4">Up to 220</td><td className="py-2">Up to 300</td></tr>
                  <tr><td className="py-2 pr-4">65&prime; × 100&prime;</td><td className="py-2 pr-4" colSpan={2}>450+ depending on layout</td></tr>
                </tbody>
              </table>
            </div>
            <p>Our team will recommend the ideal size based on your guest count, event style, and venue. <Link to="/contact" className="text-teal-500 hover:text-teal-600 underline font-medium">Contact us</Link> for a personalised recommendation.</p>
          </div>
        ),
        keywords: ['guests', 'capacity', 'size', 'seated', 'standing', 'how many'],
      },
      {
        question: 'What sizes do your tents come in?',
        answer: (
          <div className="space-y-3">
            <p>Our rental inventory includes the following sizes:</p>
            <div className="flex flex-wrap gap-2">
              {[`15' × 20'`, `20' × 20'`, `20' × 30'`, `30' × 40'`, `40' × 60'`, `65' × 50'`, `65' × 100'`].map((s) => (
                <span key={s} className="inline-block px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full font-medium">{s}</span>
              ))}
            </div>
            <p>For customers looking to <Link to="/products/custom-stretch-tents" className="text-teal-500 hover:text-teal-600 underline font-medium">purchase</Link>, custom tents are available in many additional sizes beyond our rental inventory.</p>
          </div>
        ),
        keywords: ['sizes', 'dimensions', 'inventory', 'available', 'rental', 'custom'],
      },
      {
        question: 'How do I know what size I need for a seated dinner vs. a standing reception?',
        answer: (
          <p>
            Seated dinners require significantly more space per guest than cocktail-style standing receptions. A seated dinner with round tables and service aisles needs roughly 12–15 sq ft per guest, while a standing reception can work with 6–8 sq ft per guest. We recommend <Link to="/contact" className="text-teal-500 hover:text-teal-600 underline font-medium">reaching out to our team</Link> with your guest count and intended layout — we will handle the calculations for you.
          </p>
        ),
        keywords: ['seated', 'dinner', 'standing', 'reception', 'cocktail', 'space', 'layout'],
      },
      {
        question: 'Can multiple tents be joined together?',
        answer: (
          <p>
            Yes. Stretch tents can be connected to create larger weatherproof covered spaces while maintaining a seamless, continuous appearance. This is ideal for large festivals, multi-zone corporate events, or weddings requiring separate areas for dining, dancing, and lounging — all under one unified canopy.
          </p>
        ),
        keywords: ['joined', 'connected', 'multiple', 'combined', 'larger', 'together'],
      },
    ],
  },
  {
    id: 'weather',
    title: 'Weather & Performance',
    icon: 'ri-cloud-line',
    faqs: [
      {
        question: 'Can they be used in winter or heavy rain?',
        answer: (
          <p>
            Yes. Stretch tents perform exceptionally well in wet weather when properly tensioned and installed. The waterproof fabric sheds rain effectively, and the flexible tensioning system prevents water pooling. For winter events, sidewalls and heating can be added to create a warm, comfortable environment regardless of the temperature outside.
          </p>
        ),
        keywords: ['winter', 'rain', 'cold', 'weather', 'heavy rain', 'snow'],
      },
      {
        question: 'How do they hold up in strong wind?',
        answer: (
          <p>
            Stretch tents are engineered to withstand significant wind loads when professionally installed using the correct anchoring system for the site conditions. Our team assesses each location individually and selects appropriate ground anchors, ballast weights, or hybrid systems. Safety is always the highest priority, and weather conditions are monitored throughout rental events.
          </p>
        ),
        keywords: ['wind', 'strong', 'storm', 'safety', 'anchoring', 'secure'],
      },
      {
        question: 'Can I heat the tent? Do you provide heaters?',
        answer: (
          <p>
            Yes. Heaters can be incorporated into stretch tent setups for cooler seasons. The heating solution depends on your event type, tent configuration, and venue — from discreet patio heaters for intimate gatherings to powerful industrial units for large-scale events. Discuss your requirements with our team during the planning phase.
          </p>
        ),
        keywords: ['heat', 'heaters', 'warm', 'cold weather', 'heating'],
      },
      {
        question: 'Are they suitable for hot summer events?',
        answer: (
          <p>
            Absolutely. The high-quality fabric provides excellent UV protection and shade while the open perimeter design allows continuous airflow around the tent, creating a naturally cooler environment than many traditional enclosed marquees. For particularly hot climates, additional shade sails and cooling solutions can be integrated.
          </p>
        ),
        keywords: ['summer', 'hot', 'shade', 'sun', 'uv', 'cool', 'airflow'],
      },
    ],
  },
  {
    id: 'setup',
    title: 'Setup & Site Requirements',
    icon: 'ri-tools-line',
    faqs: [
      {
        question: 'What surfaces can stretch tents be installed on?',
        answer: (
          <div className="space-y-2">
            <p>Stretch tents are remarkably versatile and can be installed on virtually any surface:</p>
            <div className="flex flex-wrap gap-2">
              {['Grass', 'Gravel', 'Concrete', 'Asphalt', 'Decking', 'Sand', 'Uneven terrain', 'Gentle slopes'].map((s) => (
                <span key={s} className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
                  <span className="w-3 h-3 flex items-center justify-center"><i className="ri-check-line text-teal-500 text-xs"></i></span>
                  {s}
                </span>
              ))}
            </div>
            <p>Every site is evaluated individually by our team to determine the optimal installation approach.</p>
          </div>
        ),
        keywords: ['surfaces', 'ground', 'grass', 'concrete', 'install', 'site'],
      },
      {
        question: 'How are tents anchored if stakes cannot be used?',
        answer: (
          <p>
            On hard surfaces such as concrete, asphalt, or decking where stakes cannot penetrate the ground, the tents are secured using engineered concrete ballast blocks or weighted anchoring systems. These are designed to provide equivalent holding force to ground stakes. Our team assesses each site and selects the appropriate anchoring method during the pre-installation survey.
          </p>
        ),
        keywords: ['anchored', 'stakes', 'concrete', 'ballast', 'hard surface', 'weight'],
      },
      {
        question: 'How much space is required around the tent?',
        answer: (
          <p>
            Generally, we recommend allowing several feet of clearance beyond the canopy footprint for poles, guy ropes, anchor points, and tensioning. The exact requirement varies depending on the tent configuration, site conditions, and chosen anchoring method. Our team will assess your venue and advise on the spatial requirements during the planning stage.
          </p>
        ),
        keywords: ['space', 'clearance', 'around', 'footprint', 'poles', 'area'],
      },
      {
        question: 'How long does installation take?',
        answer: (
          <p>
            Most standard installations are completed within a few hours on the day. Larger festival or commercial installations with multiple interconnected tents, lighting, flooring, and accessories may require one or more days. We always schedule installation in advance of your event to ensure everything is perfect and fully inspected before guests arrive.
          </p>
        ),
        keywords: ['installation', 'time', 'how long', 'setup', 'hours', 'days'],
      },
      {
        question: 'Do you need vehicle access?',
        answer: (
          <p>
            Vehicle access close to the installation site is preferred for efficient delivery and setup of equipment. However, alternative installation methods can often be arranged where access is limited — for example, in urban rooftops, gardens with narrow paths, or festival sites with restricted vehicle movement. We assess each venue individually.
          </p>
        ),
        keywords: ['vehicle', 'access', 'delivery', 'truck', 'parking', 'venue'],
      },
    ],
  },
  {
    id: 'design',
    title: 'Tent Design',
    icon: 'ri-pencil-ruler-2-line',
    faqs: [
      {
        question: 'Can the sides remain open?',
        answer: (
          <p>
            Yes — and this is one of the defining characteristics of stretch tents. They are designed as open-air structures that provide shelter overhead while maintaining unobstructed views and excellent natural airflow around the perimeter. The openness creates a connected, welcoming atmosphere that guests love.
          </p>
        ),
        keywords: ['open', 'sides', 'airflow', 'views', 'design', 'perimeter'],
      },
      {
        question: 'Can the sides be enclosed?',
        answer: (
          <p>
            Yes. Sidewalls can be added where required for weather protection, wind blocking, or seasonal use. Options include clear PVC panels that maintain visibility, solid fabric walls for privacy or shelter, or a combination of both. Sidewalls can be fully or partially installed depending on your needs.
          </p>
        ),
        keywords: ['enclosed', 'sides', 'sidewalls', 'walls', 'pvc', 'closed'],
      },
    ],
  },
  {
    id: 'pricing',
    title: 'Pricing & Booking',
    icon: 'ri-price-tag-3-line',
    faqs: [
      {
        question: 'How much does it cost to hire a stretch tent?',
        answer: (
          <div className="space-y-2">
            <p>Pricing depends on several factors:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li>Tent size and configuration</li>
              <li>Event duration (single day, weekend, or long-term)</li>
              <li>Location and travel distance from Austin</li>
              <li>Site conditions and access</li>
              <li>Additional accessories (lighting, flooring, sidewalls, heating)</li>
            </ul>
            <p>Every project is unique — we encourage you to <Link to="/contact" className="text-teal-500 hover:text-teal-600 underline font-medium">request a free customised quotation</Link> tailored to your specific event.</p>
          </div>
        ),
        keywords: ['cost', 'price', 'hire', 'rent', 'quote', 'pricing', 'fee'],
      },
      {
        question: 'What is included in the hire price?',
        answer: (
          <div className="space-y-2">
            <p>Our hire prices are comprehensive and include:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li>Delivery to your venue</li>
              <li>Professional installation by our trained team</li>
              <li>Pre-event safety inspections</li>
              <li>Event standby support (where applicable)</li>
              <li>Strike and removal after your event</li>
            </ul>
            <p>Additional accessories such as lighting, flooring, and furniture are quoted separately based on your requirements.</p>
          </div>
        ),
        keywords: ['included', 'price', 'delivery', 'installation', 'removal', 'whats included'],
      },
      {
        question: 'Do you require a deposit?',
        answer: (
          <p>
            Yes. All bookings are secured with a deposit at the time of reservation, with the remaining balance due prior to installation. The deposit amount varies depending on the project scope. Once your booking is confirmed, your date is held exclusively for you and we begin detailed planning immediately.
          </p>
        ),
        keywords: ['deposit', 'booking', 'payment', 'balance', 'secure', 'reservation'],
      },
      {
        question: 'How far in advance should I book?',
        answer: (
          <p>
            We recommend booking as early as possible, particularly for spring, summer, and autumn weekends which fill up quickly. Large festivals, weddings, and corporate events often reserve months in advance. That said, we always do our best to accommodate last-minute requests — reach out and we will let you know about availability for your date.
          </p>
        ),
        keywords: ['book', 'advance', 'when', 'early', 'reserve', 'availability', 'how far'],
      },
      {
        question: 'What is your cancellation policy?',
        answer: (
          <p>
            Cancellation terms depend on the timing relative to your event date and are clearly outlined in the hire agreement provided at the time of booking. We strive to be fair and reasonable. If you have specific concerns about cancellation, please discuss them with our team before confirming your reservation.
          </p>
        ),
        keywords: ['cancellation', 'cancel', 'refund', 'policy', 'terms'],
      },
    ],
  },
  {
    id: 'extras',
    title: 'Extras & Event Services',
    icon: 'ri-stack-line',
    faqs: [
      {
        question: 'Can I hang decorations from the tent?',
        answer: (
          <p>
            Yes, provided the weight limits and attachment points are approved by the installation team. Stretch tents can support a variety of decorative elements including fabric draping, floral installations, fairy lights, and lightweight signage. Always coordinate with our team before the event to ensure decorations are installed safely without compromising the tent structure.
          </p>
        ),
        keywords: ['decorations', 'hang', 'decor', 'lights', 'floral', 'draping'],
      },
      {
        question: 'Do you supply staff during the event?',
        answer: (
          <p>
            Yes. For many events, our team remains available on-call throughout the event to ensure the installation stays safe and looks perfect. This is particularly valuable for multi-day festivals and high-profile corporate events where weather conditions may change. On-call support can be arranged as part of your booking.
          </p>
        ),
        keywords: ['staff', 'event', 'on-call', 'support', 'team', 'during'],
      },
      {
        question: 'Do I need permits?',
        answer: (
          <p>
            Permit requirements vary depending on the venue and municipality. Some venues handle permits internally, while others require the client to obtain them. Our team is experienced with Texas event regulations and can advise on whether permits may be required for your specific location and event type.
          </p>
        ),
        keywords: ['permits', 'permission', 'legal', 'regulations', 'venue'],
      },
      {
        question: 'Do you carry public liability insurance?',
        answer: (
          <p>
            Yes. Pop Pro Tents carries appropriate public liability insurance for professional tent installations. We can provide certificates of insurance and additional insured documentation upon request — this is often required by venues and is a standard part of our professional service. Ask your event coordinator for documentation.
          </p>
        ),
        keywords: ['insurance', 'liability', 'public', 'certificate', 'coverage'],
      },
    ],
  },
];

export default function FAQSection() {
  const [searchQuery, setSearchQuery] = useState('');
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { openModal } = useQuoteModal();

  const filteredGroups = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();

    if (!q) {
      return faqGroups.map((g) => ({
        ...g,
        faqs: g.faqs,
        allMatch: true,
      }));
    }

    return faqGroups
      .map((g) => {
        const matchedFaqs = g.faqs.filter(
          (f) =>
            f.question.toLowerCase().includes(q) ||
            f.keywords.some((k) => k.includes(q))
        );
        return {
          ...g,
          faqs: matchedFaqs,
          allMatch: matchedFaqs.length === g.faqs.length,
        };
      })
      .filter((g) => g.faqs.length > 0);
  }, [searchQuery]);

  const toggleItem = (groupId: string, index: number) => {
    const key = `${groupId}-${index}`;
    setOpenItems((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleGroup = (groupId: string) => {
    const group = filteredGroups.find((g) => g.id === groupId);
    if (!group) return;

    const groupKeys = group.faqs.map((_, i) => `${groupId}-${i}`);
    const allOpen = groupKeys.every((k) => openItems.has(k));

    setOpenItems((prev) => {
      const next = new Set(prev);
      if (allOpen) {
        groupKeys.forEach((k) => next.delete(k));
      } else {
        groupKeys.forEach((k) => next.add(k));
      }
      return next;
    });
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (allOpen) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const totalVisibleFaqs = filteredGroups.reduce((sum, g) => sum + g.faqs.length, 0);

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-3xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-gray-600 text-base leading-relaxed max-w-2xl mx-auto">
            Whether you are planning a wedding, festival, corporate event, private celebration, or looking to purchase a stretch tent, we have answered the questions we hear most often. If you cannot find what you are looking for, get in touch with our team for expert advice and a free estimate.
          </p>
        </div>

        {/* Search Bar */}
        <div className="relative mb-12">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-search-line text-gray-400 text-lg"></i>
            </span>
          </div>
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder='Search FAQs (e.g. "waterproof", "pricing", "installation")'
            className="w-full pl-11 pr-10 py-4 bg-white border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent transition-shadow"
          />
          {searchQuery && (
            <button
              onClick={() => {
                setSearchQuery('');
                searchInputRef.current?.focus();
              }}
              className="absolute inset-y-0 right-0 pr-4 flex items-center cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-close-circle-line text-gray-400 text-lg hover:text-gray-600"></i>
              </span>
            </button>
          )}

          {/* Search result count */}
          {searchQuery && (
            <p className="mt-2 text-sm text-gray-500">
              {totalVisibleFaqs} {totalVisibleFaqs === 1 ? 'result' : 'results'} for &ldquo;{searchQuery}&rdquo;
            </p>
          )}
        </div>

        {/* FAQ Groups */}
        <div className="space-y-6">
          {filteredGroups.length === 0 ? (
            <div className="text-center py-16">
              <span className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-question-line text-gray-400 text-2xl"></i>
              </span>
              <h3 className="text-lg font-bold text-gray-700 mb-2">No results found</h3>
              <p className="text-gray-500 text-sm">
                Try a different search term or browse the FAQ sections below.
              </p>
              <button
                onClick={() => setSearchQuery('')}
                className="mt-4 text-teal-500 hover:text-teal-600 font-medium text-sm cursor-pointer"
              >
                Clear search
              </button>
            </div>
          ) : (
            filteredGroups.map((group) => {
              const allGroupOpen = group.faqs.every((_, i) =>
                openItems.has(`${group.id}-${i}`)
              );
              return (
                <div key={group.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                  {/* Group Header */}
                  <div className="px-6 py-5 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="w-10 h-10 bg-teal-50 rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className={`${group.icon} text-teal-500 text-lg`}></i>
                      </span>
                      <h3 className="text-lg font-bold text-gray-900">{group.title}</h3>
                      <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                        {group.faqs.length}
                      </span>
                    </div>
                    <button
                      onClick={() => toggleGroup(group.id)}
                      className="text-sm text-teal-500 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer"
                    >
                      {allGroupOpen ? 'Collapse all' : 'Expand all'}
                    </button>
                  </div>

                  {/* FAQ Items */}
                  <div className="border-t border-gray-100">
                    {group.faqs.map((faq, idx) => {
                      const key = `${group.id}-${idx}`;
                      const isOpen = openItems.has(key);

                      return (
                        <div
                          key={key}
                          className={`border-b border-gray-50 last:border-b-0 ${
                            isOpen ? 'bg-gray-50/50' : ''
                          }`}
                        >
                          <button
                            onClick={() => toggleItem(group.id, idx)}
                            className="w-full px-6 py-4 flex items-start justify-between gap-4 text-left cursor-pointer group"
                          >
                            <span className="text-sm font-semibold text-gray-800 group-hover:text-teal-600 transition-colors pr-4">
                              {faq.question}
                            </span>
                            <span
                              className={`w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5 transition-transform duration-200 ${
                                isOpen ? 'rotate-180 text-teal-500' : 'text-gray-400'
                              }`}
                            >
                              <i className="ri-arrow-down-s-line text-lg"></i>
                            </span>
                          </button>

                          {isOpen && (
                            <div className="px-6 pb-4 animate-fadeIn">
                              <div className="text-sm text-gray-600 leading-relaxed space-y-2">
                                {faq.answer}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Internal links */}
        <div className="mt-12 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/products"
            className="text-sm text-gray-500 hover:text-teal-500 transition-colors whitespace-nowrap"
          >
            Rentals
          </Link>
          <span className="text-gray-300">·</span>
          <Link
            to="/products/custom-stretch-tents"
            className="text-sm text-gray-500 hover:text-teal-500 transition-colors whitespace-nowrap"
          >
            Sales
          </Link>
          <span className="text-gray-300">·</span>
          <Link
            to="/gallery"
            className="text-sm text-gray-500 hover:text-teal-500 transition-colors whitespace-nowrap"
          >
            Gallery
          </Link>
          <span className="text-gray-300">·</span>
          <Link
            to="/contact"
            className="text-sm text-gray-500 hover:text-teal-500 transition-colors whitespace-nowrap"
          >
            Contact
          </Link>
        </div>
      </div>

      {/* Bottom CTA */}
      <div className="mt-20 bg-gradient-to-br from-teal-500 to-teal-600">
        <div className="max-w-3xl mx-auto text-center px-6 py-16">
          <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">
            Still have questions?
          </h2>
          <p className="text-lg text-white/85 mb-3 leading-relaxed max-w-2xl mx-auto">
            Every event is unique. Whether you are planning an intimate wedding, a large festival, corporate activation or looking to purchase a permanent stretch tent, our team is happy to help you find the perfect solution.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
            <button
              type="button"
              onClick={openModal}
              className="bg-white text-teal-600 hover:bg-teal-50 px-8 py-3.5 rounded-lg font-bold text-sm transition-colors whitespace-nowrap cursor-pointer"
            >
              Request a Free Quote
            </button>
            <Link
              to="/contact"
              className="bg-white/15 text-white hover:bg-white/25 border border-white/30 px-8 py-3.5 rounded-lg font-bold text-sm transition-colors whitespace-nowrap"
            >
              Speak with Our Team
            </Link>
          </div>
        </div>
      </div>

      {/* Hidden contact form for SEO / anti-spam */}
      <form
        data-readdy-form
        className="hidden"
        action="https://readdy.ai/api/form/d8v3q32181e9e92u8oeg"
        method="POST"
      >
        <input type="text" name="name" />
        <input type="email" name="email" />
        <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" />
        <textarea name="message" maxLength={500}></textarea>
      </form>

      {/* Structured Data - Schema.org FAQPage */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqGroups.flatMap((group) =>
              group.faqs.map((faq) => {
                function extractText(node: React.ReactNode): string {
                  if (typeof node === 'string') return node;
                  if (typeof node === 'number') return String(node);
                  if (!node) return '';
                  if (Array.isArray(node)) return node.map(extractText).join(' ');
                  if (typeof node === 'object' && 'props' in node) {
                    return extractText((node.props as { children?: React.ReactNode }).children);
                  }
                  return '';
                }
                return {
                  '@type': 'Question',
                  name: faq.question,
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: extractText(faq.answer).replace(/\s+/g, ' ').trim(),
                  },
                };
              })
            ),
          }),
        }}
      />
    </section>
  );
}